import { Keyboard, Mic, MonitorUp, Palette, Settings, Users, Video, Volume2, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LIMITS } from '../../shared/protocol';
import { LevelBar, SwitchRow, useTrackStream, VideoView } from '../components/ui';
import { playTestSound } from '../lib/audio';
import { deviceLabel, describeMediaError, getCameraTrack, getMicrophoneTrack, refreshDevices, supports, useDevices } from '../lib/media';
import { updateSettings, useSettings, type Theme } from '../lib/settings';
import { toast } from '../lib/toast';
import '../styles/settings.css';

export type SettingsTab = 'general' | 'video' | 'audio' | 'meetings' | 'share' | 'shortcuts';

const TABS: { id: SettingsTab; label: string; icon: ReactNode }[] = [
  { id: 'general', label: 'General', icon: <Settings size={16} /> },
  { id: 'video', label: 'Vídeo', icon: <Video size={16} /> },
  { id: 'audio', label: 'Audio', icon: <Volume2 size={16} /> },
  { id: 'meetings', label: 'Reuniones', icon: <Users size={16} /> },
  { id: 'share', label: 'Compartir pantalla', icon: <MonitorUp size={16} /> },
  { id: 'shortcuts', label: 'Atajos de teclado', icon: <Keyboard size={16} /> },
];

/**
 * @param inMeeting When true the preview reuses nothing from the call; it opens its own
 *                  short-lived tracks and closes them when the dialog closes.
 */
export function SettingsDialog({ onClose, initialTab = 'general' }: { onClose: () => void; initialTab?: SettingsTab }) {
  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    void refreshDevices();
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide settings" role="dialog" aria-modal="true" aria-label="Configuración">
        <nav className="settings-nav" aria-label="Secciones de configuración">
          <h2>Configuración</h2>
          {TABS.map((t) => (
            <button key={t.id} type="button" className="settings-tab" aria-current={tab === t.id ? 'page' : undefined} onClick={() => setTab(t.id)}>
              {t.icon}
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
        <section className="settings-content">
          <button type="button" className="icon-btn settings-close" aria-label="Cerrar configuración" onClick={onClose}>
            <X size={18} />
          </button>
          {tab === 'general' && <GeneralTab />}
          {tab === 'video' && <VideoTab />}
          {tab === 'audio' && <AudioTab />}
          {tab === 'meetings' && <MeetingsTab />}
          {tab === 'share' && <ShareTab />}
          {tab === 'shortcuts' && <ShortcutsTab />}
        </section>
      </div>
    </div>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="settings-section">
      <h3>{title}</h3>
      <div className="settings-card">{children}</div>
    </div>
  );
}

function GeneralTab() {
  const s = useSettings();
  const [name, setName] = useState(s.displayName);
  const themes: { id: Theme; label: string }[] = [
    { id: 'light', label: 'Claro' },
    { id: 'dark', label: 'Oscuro' },
    { id: 'system', label: 'Sistema' },
  ];
  return (
    <>
      <Section title="Perfil">
        <label className="field">
          <span>Nombre visible</span>
          <input
            className="input"
            value={name}
            maxLength={LIMITS.nameLength}
            placeholder="Tu nombre"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => updateSettings({ displayName: name.trim() })}
          />
          <span className="hint">Se usa al crear o unirte a reuniones. Solo se guarda en este navegador.</span>
        </label>
      </Section>
      <Section title="Aspecto">
        <div className="theme-picker" role="radiogroup" aria-label="Tema">
          {themes.map((t) => (
            <button key={t.id} type="button" role="radio" aria-checked={s.theme === t.id} className={`theme-option theme-${t.id}`} onClick={() => updateSettings({ theme: t.id })}>
              <span className="theme-swatch" aria-hidden>
                <span />
                <span />
              </span>
              <Palette size={14} aria-hidden /> {t.label}
            </button>
          ))}
        </div>
        <p className="hint" style={{ margin: '10px 0 0' }}>La sala de reunión usa siempre el tema oscuro para que el vídeo destaque.</p>
      </Section>
    </>
  );
}

function DeviceSelect({ label, value, devices, fallback, onChange, empty }: { label: string; value: string; devices: MediaDeviceInfo[]; fallback: string; onChange: (id: string) => void; empty: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)} disabled={!devices.length}>
        {!devices.length && <option>{empty}</option>}
        {devices.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId}>
            {deviceLabel(d, i, fallback)}
          </option>
        ))}
      </select>
    </label>
  );
}

function VideoTab() {
  const s = useSettings();
  const devices = useDevices();
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stream = useTrackStream(track);

  useEffect(() => {
    let active: MediaStreamTrack | null = null;
    let cancelled = false;
    setError(null);
    getCameraTrack(s)
      .then((t) => {
        if (cancelled) return t.stop();
        active = t;
        setTrack(t);
      })
      .catch((err) => !cancelled && setError(describeMediaError(err, 'camera')));
    return () => {
      cancelled = true;
      active?.stop();
      setTrack(null);
    };
    // Re-open the preview only when the device or resolution changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.videoInputId, s.hdVideo]);

  const selected = s.videoInputId || track?.getSettings().deviceId || devices.videoInputs[0]?.deviceId || '';
  const dims = track?.getSettings();
  return (
    <>
      <div className="settings-preview">
        {stream ? <VideoView stream={stream} mirror={s.mirrorVideo} /> : <div className="preview-placeholder">{error ?? 'Abriendo la cámara…'}</div>}
        {dims?.width && (
          <span className="preview-res">
            {dims.width}×{dims.height}
          </span>
        )}
      </div>
      <Section title="Cámara">
        <DeviceSelect label="Cámara" value={selected} devices={devices.videoInputs} fallback="Cámara" empty="No se encontró ninguna cámara" onChange={(id) => updateSettings({ videoInputId: id })} />
        <SwitchRow label="Vídeo HD" description="Más calidad a cambio de más ancho de banda." checked={s.hdVideo} onChange={(v) => updateSettings({ hdVideo: v })} />
        <SwitchRow label="Reflejar mi vídeo" description="Solo afecta a cómo te ves tú; los demás te ven sin reflejar." checked={s.mirrorVideo} onChange={(v) => updateSettings({ mirrorVideo: v })} />
        <SwitchRow label="Ocultar mi propia vista" checked={s.hideSelfView} onChange={(v) => updateSettings({ hideSelfView: v })} />
      </Section>
    </>
  );
}

function AudioTab() {
  const s = useSettings();
  const devices = useDevices();
  const [mic, setMic] = useState<MediaStreamTrack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    let active: MediaStreamTrack | null = null;
    let cancelled = false;
    setError(null);
    getMicrophoneTrack(s)
      .then((t) => {
        if (cancelled) return t.stop();
        active = t;
        setMic(t);
      })
      .catch((err) => !cancelled && setError(describeMediaError(err, 'microphone')));
    return () => {
      cancelled = true;
      active?.stop();
      setMic(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.audioInputId, s.echoCancellation, s.noiseSuppression, s.autoGainControl]);

  const testSpeaker = async () => {
    setTesting(true);
    try {
      await playTestSound(s.audioOutputId);
    } catch {
      toast('No se pudo reproducir el sonido de prueba en ese altavoz', 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <Section title="Altavoz">
        {supports.outputSelection ? (
          <DeviceSelect label="Salida de audio" value={s.audioOutputId} devices={devices.audioOutputs} fallback="Altavoz" empty="Salida predeterminada" onChange={(id) => updateSettings({ audioOutputId: id })} />
        ) : (
          <p className="hint">Este navegador usa siempre la salida de audio del sistema.</p>
        )}
        <div className="row" style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={testSpeaker} disabled={testing}>
            <Volume2 size={14} /> {testing ? 'Reproduciendo…' : 'Probar altavoz'}
          </button>
        </div>
      </Section>
      <Section title="Micrófono">
        <DeviceSelect label="Entrada de audio" value={s.audioInputId} devices={devices.audioInputs} fallback="Micrófono" empty="No se encontró ningún micrófono" onChange={(id) => updateSettings({ audioInputId: id })} />
        <div className="field" style={{ marginTop: 12 }}>
          <span className="label">
            <Mic size={13} style={{ verticalAlign: -2 }} /> Nivel de entrada — habla para probarlo
          </span>
          {error ? <span className="error-text">{error}</span> : <LevelBar track={mic} bars={20} />}
        </div>
      </Section>
      <Section title="Procesamiento">
        <SwitchRow label="Supresión de ruido" checked={s.noiseSuppression} onChange={(v) => updateSettings({ noiseSuppression: v })} />
        <SwitchRow label="Cancelación de eco" checked={s.echoCancellation} onChange={(v) => updateSettings({ echoCancellation: v })} />
        <SwitchRow label="Ajustar volumen del micrófono automáticamente" checked={s.autoGainControl} onChange={(v) => updateSettings({ autoGainControl: v })} />
      </Section>
    </>
  );
}

function MeetingsTab() {
  const s = useSettings();
  return (
    <>
      <Section title="Al unirse a una reunión">
        <SwitchRow label="Mostrar primero la vista previa del vídeo" checked={s.showPreview} onChange={(v) => updateSettings({ showPreview: v })} />
        <SwitchRow label="Mantener mi cámara apagada" checked={s.joinVideoOff} onChange={(v) => updateSettings({ joinVideoOff: v })} />
        <SwitchRow label="Mantener mi micrófono silenciado" checked={s.joinMuted} onChange={(v) => updateSettings({ joinMuted: v })} />
      </Section>
      <Section title="Durante la reunión">
        <SwitchRow label="Mostrar nombres de los participantes" checked={s.showNames} onChange={(v) => updateSettings({ showNames: v })} />
        <SwitchRow label="Mostrar temporizador de la reunión" checked={s.showTimer} onChange={(v) => updateSettings({ showTimer: v })} />
        <SwitchRow label="Pedir confirmación al salir" checked={s.confirmLeave} onChange={(v) => updateSettings({ confirmLeave: v })} />
        <SwitchRow label="Avisar cuando alguien entra o sale" checked={s.notifyJoinLeave} onChange={(v) => updateSettings({ notifyJoinLeave: v })} />
        <SwitchRow label="Sonido al recibir mensajes de chat" checked={s.chatSound} onChange={(v) => updateSettings({ chatSound: v })} />
      </Section>
    </>
  );
}

function ShareTab() {
  const s = useSettings();
  return (
    <Section title="Cuando comparto">
      <SwitchRow label="Mostrar un borde verde mientras comparto" checked={s.shareBorder} onChange={(v) => updateSettings({ shareBorder: v })} />
      <p className="hint" style={{ margin: '12px 0 0' }}>
        Al compartir, el navegador te deja elegir la pantalla completa, una ventana concreta o una pestaña. {supports.screenShare ? '' : 'Este navegador no admite compartir pantalla.'}
      </p>
    </Section>
  );
}

const SHORTCUTS: [string, string][] = [
  ['Alt + A', 'Silenciar / reactivar el audio'],
  ['Alt + V', 'Iniciar / detener el vídeo'],
  ['Alt + S', 'Compartir / dejar de compartir pantalla'],
  ['Alt + H', 'Mostrar / ocultar el chat'],
  ['Alt + U', 'Mostrar / ocultar participantes'],
  ['Alt + Y', 'Levantar / bajar la mano'],
  ['Alt + F', 'Pantalla completa'],
  ['Mantener Espacio', 'Pulsar para hablar mientras estás silenciado'],
];

function ShortcutsTab() {
  return (
    <Section title="Atajos en la reunión">
      <table className="shortcuts">
        <tbody>
          {SHORTCUTS.map(([keys, what]) => (
            <tr key={keys}>
              <td>
                <kbd className="kbd">{keys}</kbd>
              </td>
              <td>{what}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}
